import React, { memo, useEffect, useState } from 'react'
import { Customer } from 'apps/fleet-planner/components/form-components/Customer'
import { CustomerDetails } from 'apps/fleet-planner/components/load-details/Details'
import { LockedTooltip } from 'apps/fleet-planner/components/load-details/LockedTooltip'
import { useShipmentApi } from 'apps/fleet-planner/hooks/useShipmentApi'
import { ICustomer, ICustomerContact, ILoad } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'
import { LOAD_FIELDS } from 'apps/fleet-planner/utils/permissions'
import { Button, Card, InlineEditContainer } from 'transfix-ui'

interface ICustomerInlineEditProps {
  customer?: ICustomer
  customerContact?: ICustomerContact
  currentEditField?: string
  customerNotes?: string
  isLocked?: boolean
  onEditModeRequested: (name?: string) => void
  onChange: (change: Partial<ILoad>) => void
  onSave: () => void
  onCancel: () => void
  isExternal?: boolean
  isLoading?: boolean
}

export const CustomerInlineEdit = memo<ICustomerInlineEditProps>(
  ({
    currentEditField,
    customer: customerProp,
    customerContact: customerContactProp,
    customerNotes: customerNotesProp = '',
    customerNotes,
    isExternal,
    isLoading,
    isLocked,
    onCancel,
    onEditModeRequested,
  }) => {
    const NAME = LOAD_FIELDS.customers
    const isEditMode = currentEditField === NAME

    const [localChanges, setLocalChanges] = useState<
      Pick<Types.ILoad, 'customer' | 'customerContact' | 'customerNotes'>
    >({
      customer: customerProp,
      customerContact: customerContactProp,
      customerNotes: customerNotesProp,
    })

    const { updateShipment } = useShipmentApi()
    const [validationMessage, setValidationMessage] = useState<string | undefined>('')

    useEffect(() => {
      setLocalChanges(prev => ({ ...prev, customer: customerProp as Types.ICustomer }))
    }, [customerProp])

    useEffect(() => {
      setLocalChanges(prev => ({
        ...prev,
        customerContact: customerContactProp as Types.ICustomerContact,
      }))
    }, [customerContactProp])

    useEffect(() => {
      setLocalChanges(prev => ({ ...prev, customerNotes: customerNotesProp }))
    }, [customerNotesProp])

    const changeHandler = (change: Partial<Types.ILoad>) => {
      setLocalChanges(prev => ({
        ...prev,
        ...change,
      }))
    }

    const onSave = () => {
      onEditModeRequested('')
      updateShipment({
        ...localChanges,
      })
    }

    const resetLocalChanges = () => {
      setLocalChanges({
        customer: customerProp,
        customerContact: customerContactProp,
        customerNotes: customerNotesProp,
      })
    }

    const editModeRequestedHandler = (name?: string) => {
      if (!name) {
        resetLocalChanges()
      }
      onEditModeRequested(name)
    }

    return (
      <InlineEditContainer
        isLoading={isLoading}
        isLocked={isLocked}
        lockedIcon={<LockedTooltip className='locked-icon' />}
        dataTest='load-details-customer-section'
        name={NAME}
        editModeEnabled={isEditMode}
        onEditModeRequested={editModeRequestedHandler}
        resetOnClickOutside={false}
        editMode={
          <>
            {isEditMode && (
              <Card className='pa4'>
                <Customer
                  customer={localChanges?.customer}
                  customerContact={localChanges?.customerContact}
                  customerNotes={localChanges?.customerNotes}
                  onChange={changeHandler}
                  onValidationChange={setValidationMessage}
                />
                <div className='tr'>
                  <div className='mt4'>
                    <Button color='tertiary' size='large' onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button
                      color='primary'
                      size='large'
                      disabled={!!validationMessage}
                      onClick={onSave}
                      className='ml2'
                      data-test='save-new-customer-button'>
                      Save
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </>
        }>
        <CustomerDetails
          customer={customerProp}
          customerContact={customerContactProp}
          customerNotes={customerNotes}
          isExternal={isExternal}
        />
      </InlineEditContainer>
    )
  }
)
